import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import PlatformIcon, { SOCIAL_PLATFORMS, inferPlatform, platformLabel, type SocialPlatform } from '../components/PlatformIcon'
import HeroC from '../components/HeroC'
import { useAuth } from '../contexts/AuthContext'
import type { BenefitListSection, ContentSection, EconomicsSection, GuestRosterSection, ImageGridSection, MediaSection, PackageBlockSection, SocialsSection, SponsorConfig, TextSection, ValueProofSection } from '../shared/tokens/sponsor-config.types'
import { buildAudienceSponsorConfig, MASTER_AUDIENCE_FALLBACK, MASTER_HERO_SOCIAL_LINKS } from '../lib/masterAudience'
import { AUDIENCE_PREVIEW_TAB, isSponsorPitchTabSlug, sponsorPitchTabForSlug, SPONSOR_PITCH_TABS } from '../lib/sponsorPitchRegistry'

interface SponsorVersion {
  id: string
  version: number
  state: 'draft' | 'published' | 'archived'
  data?: SponsorConfig
  created_at?: string
  created_by?: string | null
  created_by_email?: string | null
  created_by_name?: string | null
  published_at?: string | null
  published_by?: string | null
  published_by_email?: string | null
  published_by_name?: string | null
  approved_at?: string | null
  approved_by?: string | null
  approved_by_email?: string | null
  approved_by_name?: string | null
  archived_at?: string | null
  archived_by?: string | null
  archived_by_email?: string | null
  archived_by_name?: string | null
  approval_status?: 'pending' | 'approved' | 'rejected'
  restored_from_version_id?: string | null
  change_summary?: string | null
  content_hash?: string | null
}

interface SponsorPageResponse {
  page?: {
    slug: string
    base_slug?: string | null
    title?: string | null
    status?: string | null
    published_version_id?: string | null
    updated_at?: string | null
  } | null
  draft?: SponsorVersion | null
  published?: SponsorVersion | null
  fallback?: SponsorConfig | null
  source?: string
  warning?: string
  error?: string
}

interface SponsorVersionsResponse {
  versions?: SponsorVersion[]
  error?: string
}

interface MediaUploadResponse {
  url?: string
  mediaType?: 'image' | 'video'
  error?: string
}

interface UploadProgressState {
  key: string
  fileName: string
  fileSize: number
  progress: number | null
}

interface AccordionPanelProps {
  id: string
  eyebrow?: string
  title: string
  summary?: string
  chips?: string[]
  open: boolean
  onToggle: () => void
  children: ReactNode
}

const MAX_HERO_VIDEO_BYTES = 60 * 1024 * 1024
const MAX_HERO_IMAGE_BYTES = 12 * 1024 * 1024
const ALLOWED_VIDEO_TYPES = new Set(['video/mp4', 'video/webm', 'video/quicktime'])
const ALLOWED_PRIMARY_VIDEO_TYPES = new Set(['video/mp4', 'video/quicktime'])
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif'])
const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/avif'
const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime'
const PRIMARY_VIDEO_ACCEPT = 'video/mp4,video/quicktime'

function acceptForMediaType(mediaType: 'image' | 'video') {
  return mediaType === 'image' ? IMAGE_ACCEPT : VIDEO_ACCEPT
}

function uploadTypeError(mediaType: 'image' | 'video', allowedTypes: Set<string>) {
  if (mediaType === 'image') return 'Upload a JPG, PNG, WebP, or AVIF image.'
  if (allowedTypes === ALLOWED_PRIMARY_VIDEO_TYPES) return 'Upload an MP4 or MOV video. Use the WebM fallback field for WebM.'
  return 'Upload an MP4, WebM, or MOV video.'
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'Not published'
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function formatFileSize(bytes: number) {
  const formatter = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 })
  if (bytes < 1024) return `${formatter.format(bytes)} B`
  if (bytes < 1024 * 1024) return `${formatter.format(bytes / 1024)} KB`
  return `${formatter.format(bytes / (1024 * 1024))} MB`
}

function cloneConfig(config: SponsorConfig) {
  return structuredClone(config) as SponsorConfig
}

function lineList(value: string) {
  return value.split('\n').map(line => line.trim()).filter(Boolean)
}

function formatLinks(links: Array<{ label: string; href: string }> | undefined) {
  return (links ?? []).map(link => `${link.label} | ${link.href}`).join('\n')
}

function pipeLinkList(value: string) {
  return lineList(value).map(line => {
    const [label = '', ...rest] = line.split('|')
    return { label: label.trim(), href: rest.join('|').trim() }
  }).filter(link => link.label && link.href)
}

function formatValueProofMetrics(metrics: Array<{ label: string; value: string }> | undefined) {
  return (metrics ?? []).map(metric => `${metric.label} | ${metric.value}`).join('\n')
}

function pipeValueProofMetrics(value: string) {
  return lineList(value).map(line => {
    const [label = '', ...rest] = line.split('|')
    return { label: label.trim(), value: rest.join('|').trim() }
  }).filter(metric => metric.label || metric.value)
}

function newSection(type: ContentSection['type']): ContentSection {
  const id = `${type}-${Date.now()}`
  if (type === 'socials') return { id, type, heading: 'Socials', body: '', platforms: [] }
  if (type === 'value-proof') {
    return {
      id,
      type,
      eyebrow: 'Sponsor social value',
      heading: 'You are not buying a post. You are entering the season.',
      body: '',
      cards: [],
      current_view: { label: 'Current view surface', stat: '', body: '', items: [] },
    }
  }
  if (type === 'benefit-list') return { id, type, heading: 'New benefit section', body: '', benefits: [] }
  if (type === 'package-block') return { id, type, heading: 'Packages', body: '', packages: [] }
  if (type === 'media') return { id, type, heading: 'Media', media_type: 'image', url: '', alt: '', caption: '' }
  if (type === 'image-grid') return { id, type, heading: 'Images', body: '', images: [] }
  if (type === 'quote') return { id, type, text: '', attribution: '' }
  return { id, type: 'text', heading: 'New section', body: '' }
}

function sectionLabel(section: ContentSection, index: number) {
  if ('heading' in section && section.heading) return section.heading
  if (section.type === 'quote') return section.text ? `Quote: ${section.text.slice(0, 32)}` : 'Quote'
  return `${section.type} ${index + 1}`
}

function sectionKey(section: ContentSection, index: number) {
  return `${section.id ?? section.type}-${index}`
}

function countWords(value = '') {
  return value.trim().split(/\s+/).filter(Boolean).length
}

function sectionChips(section: ContentSection) {
  if (section.type === 'text') return [`${countWords(section.body)} words`, section.heading ? 'Heading set' : 'No heading']
  if (section.type === 'socials') return [`${section.platforms.length} platforms`, section.platforms.some(platform => !platform.href) ? 'Links pending' : 'Links set']
  if (section.type === 'value-proof') return [`${section.cards.length} proof cards`, section.current_view ? 'View panel set' : 'No view panel']
  if (section.type === 'benefit-list') return [`${section.benefits.length} benefits`, section.body ? 'Intro set' : 'No intro']
  if (section.type === 'package-block') return [`${section.packages.length} packages`, section.packages.some(pkg => !pkg.price) ? 'Price pending' : 'Pricing set']
  if (section.type === 'media') return [section.media_type, section.url ? 'URL set' : 'URL missing', section.media_type === 'image' && !section.alt ? 'Alt missing' : 'Alt set']
  if (section.type === 'image-grid') return [`${section.images.length} images`, section.images.some(image => !image.alt) ? 'Alt pending' : 'Alt set']
  if (section.type === 'quote') return [section.attribution ? 'Attribution set' : 'No attribution']
  return [section.type]
}

function AccordionPanel({ id, eyebrow, title, summary, chips = [], open, onToggle, children }: AccordionPanelProps) {
  const buttonId = `${id}-button`
  const panelId = `${id}-panel`
  return (
    <section className="admin-accordion-panel">
      <h2 className="admin-accordion-heading">
        <button id={buttonId} type="button" aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
          <span className="admin-accordion-title-wrap">
            {eyebrow && <span className="legal-eyebrow">{eyebrow}</span>}
            <span className="admin-accordion-title">{title}</span>
            {summary && <span className="admin-accordion-summary">{summary}</span>}
          </span>
          <span className="admin-accordion-meta">
            {chips.slice(0, 3).map(chip => <span className="admin-chip" key={chip}>{chip}</span>)}
            <span className="admin-accordion-icon" aria-hidden="true">{open ? '-' : '+'}</span>
          </span>
        </button>
      </h2>
      {open && <div id={panelId} className="admin-accordion-content" role="region" aria-labelledby={buttonId}>{children}</div>}
    </section>
  )
}

function VersionIcon() {
  return (
    <svg className="admin-version-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 7h8" />
      <path d="M6 12h12" />
      <path d="M6 17h7" />
      <path d="M17 15l2 2 3-4" />
    </svg>
  )
}

function VersionActionIcon({ type }: { type: 'approve' | 'restore' | 'publish' | 'archive' }) {
  if (type === 'approve') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l4 4L19 6" /></svg>
  }
  if (type === 'restore') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 7h7a5 5 0 1 1-3.5 8.5" /><path d="M7 7v5" /><path d="M7 7h5" /></svg>
  }
  if (type === 'archive') {
    return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16" /><path d="M6 7v12h12V7" /><path d="M9 11h6" /></svg>
  }
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19V5" /><path d="M7 10l5-5 5 5" /></svg>
}

function actorName(version: SponsorVersion, action: 'created' | 'approved' | 'published' | 'archived') {
  const name = version[`${action}_by_name` as keyof SponsorVersion]
  const email = version[`${action}_by_email` as keyof SponsorVersion]
  return typeof name === 'string' && name ? name : (typeof email === 'string' && email ? email : 'Unknown')
}

function sourceStatusLabel(source: string) {
  if (source === 'database') return 'Database content'
  if (source === 'static preview' || source === 'static') return 'Using fallback content'
  if (source === 'migration required') return 'Migration needed'
  if (source === 'loading') return 'Loading'
  return source
}

function draftStatusLabel(version: SponsorVersion | null) {
  return version ? `Draft v${version.version}` : 'No saved draft'
}

function publishedStatusLabel(version: SponsorVersion | null) {
  return version ? `Published v${version.version}` : 'No database publish'
}

function uploadStatusId(key: string) {
  return `upload-status-${key.replace(/[^a-z0-9]+/gi, '-')}`
}

function renderAudienceIntro(config: SponsorConfig) {
  const section = config.content.sections.find((item) => item.type === 'text')
  if (!section || section.type !== 'text') return null

  return (
    <section className="section-text">
      {section.heading && <h2>{section.heading}</h2>}
      {section.body.split(/\n{2,}/).map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </section>
  )
}

function parseMediaUploadResponse(xhr: XMLHttpRequest): MediaUploadResponse {
  if (xhr.response && typeof xhr.response === 'object') return xhr.response as MediaUploadResponse
  let responseText = ''
  try {
    responseText = xhr.responseText
  } catch {
    return {}
  }
  if (!responseText) return {}
  try {
    return JSON.parse(responseText) as MediaUploadResponse
  } catch {
    return {}
  }
}

function uploadMediaWithProgress(formData: FormData, onProgress: (progress: number | null) => void) {
  return new Promise<MediaUploadResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/admin/content/media')
    xhr.withCredentials = true
    xhr.responseType = 'json'
    xhr.upload.onprogress = event => {
      if (!event.lengthComputable || event.total <= 0) {
        onProgress(null)
        return
      }
      onProgress(Math.min(99, Math.max(1, Math.round((event.loaded / event.total) * 100))))
    }
    xhr.onload = () => {
      const data = parseMediaUploadResponse(xhr)
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(data.error ?? 'Upload failed'))
        return
      }
      onProgress(100)
      resolve(data)
    }
    xhr.onerror = () => reject(new Error('Upload failed'))
    xhr.onabort = () => reject(new Error('Upload cancelled'))
    xhr.send(formData)
  })
}

export default function AdminSponsorPageEditor() {
  const params = useParams<{ slug: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { isLoggedIn, loading, status, rights } = useAuth()
  const [config, setConfig] = useState<SponsorConfig | null>(null)
  const [pageMeta, setPageMeta] = useState<SponsorPageResponse['page']>(null)
  const [draftVersion, setDraftVersion] = useState<SponsorVersion | null>(null)
  const [publishedVersion, setPublishedVersion] = useState<SponsorVersion | null>(null)
  const [versions, setVersions] = useState<SponsorVersion[]>([])
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [source, setSource] = useState('loading')
  const [loadingPage, setLoadingPage] = useState(true)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [archivingVersionId, setArchivingVersionId] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<UploadProgressState | null>(null)
  const [dirty, setDirty] = useState(false)
  const [versionPopoverOpen, setVersionPopoverOpen] = useState(false)
  const [openPanels, setOpenPanels] = useState<Record<string, boolean>>({
    page: false,
    hero: true,
    brand: false,
    sections: true,
    notes: false,
  })
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const versionButtonRef = useRef<HTMLButtonElement | null>(null)
  const versionPopoverRef = useRef<HTMLDivElement | null>(null)

  const canAdmin = isLoggedIn && status === 'approved' && rights['admin.content.manage'] === true
  const routeSlug = params.slug ?? 'master'
  const activeSlug = isSponsorPitchTabSlug(routeSlug) ? routeSlug : 'master'
  const previewMode = new URLSearchParams(location.search).get('view') === 'audience'
  const slug = config?.slug ?? activeSlug
  const activeTab = previewMode ? AUDIENCE_PREVIEW_TAB.key : activeSlug
  const activePitch = sponsorPitchTabForSlug(activeSlug)
  const editorTitle = activeSlug === 'master' ? 'Master Pitch CMS' : `${activePitch.label} Pitch CMS`
  const editorEyebrow = activeSlug === 'master' ? 'CMS · Master Pitch' : `CMS · ${activePitch.label}`
  const audiencePreviewConfig = useMemo(
    () => buildAudienceSponsorConfig(config ?? draftVersion?.data ?? publishedVersion?.data ?? MASTER_AUDIENCE_FALLBACK),
    [config, draftVersion?.data, publishedVersion?.data],
  )
  const uploadingKey = uploadProgress?.key ?? null

  async function loadVersions() {
    setVersionsLoading(true)
    try {
      const res = await fetch(`/api/admin/sponsor-pages/${encodeURIComponent(activeSlug)}/versions?limit=30`, { credentials: 'include' })
      const data = await res.json() as SponsorVersionsResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to load versions')
      setVersions(data.versions ?? [])
    } catch {
      setVersions([])
    } finally {
      setVersionsLoading(false)
    }
  }

  async function loadPage() {
    setLoadingPage(true)
    setError(null)
    try {
      const res = await fetch(`/api/admin/sponsor-pages/${encodeURIComponent(activeSlug)}`, { credentials: 'include' })
      const data = await res.json() as SponsorPageResponse
      if (!res.ok) throw new Error(data.error ?? `Unable to load ${activePitch.label}`)
      const nextConfig = data.draft?.data ?? data.published?.data ?? data.fallback ?? null
      setPageMeta(data.page ?? null)
      setConfig(nextConfig)
      setDraftVersion(data.draft ?? null)
      setPublishedVersion(data.published ?? null)
      setDirty(false)
      setSource(data.warning === 'migration_required' ? 'migration required' : (data.warning === 'database_not_configured' ? 'static preview' : (data.source ?? 'static')))
      if (data.warning === 'database_not_configured') {
        setMessage('Local preview is using static fallback content because Supabase bindings are not configured. Save and publish will work after local bindings and migrations are available.')
      } else if (data.warning === 'migration_required') {
        setMessage('CMS tables are not available yet. Apply the sponsor page migration before saving or publishing.')
      }
      void loadVersions()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Unable to load ${activePitch.label}`)
    } finally {
      setLoadingPage(false)
    }
  }

  useEffect(() => {
    if (canAdmin) void loadPage()
  }, [canAdmin, activeSlug])

  useEffect(() => {
    if (params.slug && !isSponsorPitchTabSlug(params.slug)) navigate('/admin/sponsor-pages/master', { replace: true })
  }, [navigate, params.slug])

  useEffect(() => {
    if (!versionPopoverOpen) return undefined

    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node | null
      if (target && (versionButtonRef.current?.contains(target) || versionPopoverRef.current?.contains(target))) return
      setVersionPopoverOpen(false)
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setVersionPopoverOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [versionPopoverOpen])

  function updateConfig(mutator: (draft: SponsorConfig) => void) {
    setDirty(true)
    setConfig(prev => {
      if (!prev) return prev
      const next = cloneConfig(prev)
      mutator(next)
      return next
    })
  }

  // Hero-layout px dials: parse "-15px" <-> number; clamp()/responsive defaults show blank.
  function pxToNum(value?: string): number | '' {
    if (!value) return ''
    const m = /^(-?\d+(?:\.\d+)?)px$/.exec(value.trim())
    return m ? Number(m[1]) : ''
  }
  function setLayoutField(key: 'sponsor_shift_x' | 'separator_offset_x' | 'midline_shift' | 'logo_max_height' | 'separator_color', value: string) {
    updateConfig(next => {
      const layout: Record<string, string> = { ...(next.content.hero_layout ?? {}) }
      if (value) layout[key] = value
      else delete layout[key]
      next.content.hero_layout = Object.keys(layout).length ? layout : undefined
    })
  }
  function setLayoutPx(key: 'sponsor_shift_x' | 'separator_offset_x' | 'midline_shift' | 'logo_max_height', raw: string) {
    setLayoutField(key, raw === '' ? '' : `${Number(raw)}px`)
  }

  function togglePanel(panel: string) {
    setOpenPanels(prev => ({ ...prev, [panel]: !prev[panel] }))
  }

  function toggleSection(key: string) {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }))
  }

  function toggleVersionPopover() {
    const nextOpen = !versionPopoverOpen
    setVersionPopoverOpen(nextOpen)
    if (nextOpen) void loadVersions()
  }

  function openEditorTab(nextSlug: string) {
    if (dirty && !window.confirm('Discard unsaved changes and switch tabs?')) return
    setVersionPopoverOpen(false)
    navigate(`/admin/sponsor-pages/${nextSlug}`)
  }

  function openAudiencePreview() {
    if (dirty && !window.confirm('Discard unsaved changes and open the Master audience preview?')) return
    setVersionPopoverOpen(false)
    navigate('/admin/sponsor-pages/master?view=audience')
  }

  function updateSection(index: number, section: ContentSection) {
    updateConfig(next => {
      next.content.sections[index] = section
    })
  }

  function moveSection(index: number, direction: -1 | 1) {
    updateConfig(next => {
      const target = index + direction
      if (target < 0 || target >= next.content.sections.length) return
      const sections = [...next.content.sections]
      const current = sections[index]
      sections[index] = sections[target]
      sections[target] = current
      next.content.sections = sections
    })
  }

  function removeSection(index: number) {
    updateConfig(next => {
      next.content.sections = next.content.sections.filter((_, i) => i !== index)
    })
  }

  async function saveDraft(showMessage = true) {
    if (!config) return null
    setSaving(true)
    setError(null)
    if (showMessage) setMessage(null)
    try {
      const res = await fetch(`/api/admin/sponsor-pages/${encodeURIComponent(config.slug)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ config }),
      })
      const data = await res.json() as { draft?: SponsorVersion; error?: string }
      if (!res.ok || !data.draft?.data) throw new Error(data.error ?? 'Unable to save draft')
      setDraftVersion(data.draft)
      setConfig(data.draft.data)
      setSource('database')
      setDirty(false)
      if (showMessage) setMessage(`Draft v${data.draft.version} saved.`)
      void loadVersions()
      return data.draft
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save draft')
      return null
    } finally {
      setSaving(false)
    }
  }

  async function publishDraft(versionId?: string) {
    setPublishing(true)
    setError(null)
    setMessage(null)
    try {
      const saved = versionId ? null : await saveDraft(false)
      if (!versionId && !saved) throw new Error('Save the draft before publishing.')
      const publishSlug = versionId ? slug : (saved?.data?.slug ?? slug)
      const res = await fetch(`/api/admin/sponsor-pages/${encodeURIComponent(publishSlug)}/publish`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(versionId ? { versionId } : {}),
      })
      const data = await res.json() as { published?: SponsorVersion; error?: string }
      if (!res.ok || !data.published?.data) throw new Error(data.error ?? 'Unable to publish draft')
      setPublishedVersion(data.published)
      setDraftVersion(null)
      setConfig(data.published.data)
      setSource('database')
      setDirty(false)
      setMessage(`Published v${data.published.version}.`)
      void loadVersions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to publish draft')
    } finally {
      setPublishing(false)
    }
  }

  async function approveVersion(versionId: string) {
    setError(null)
    setMessage(null)
    try {
      const res = await fetch(`/api/admin/sponsor-pages/${encodeURIComponent(slug)}/versions/${encodeURIComponent(versionId)}/approve`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: `Approved in ${editorTitle}.` }),
      })
      const data = await res.json() as { version?: SponsorVersion; error?: string }
      if (!res.ok || !data.version) throw new Error(data.error ?? 'Unable to approve version')
      setMessage(`Approved v${data.version.version}.`)
      void loadVersions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to approve version')
    }
  }

  async function restoreVersion(version: SponsorVersion) {
    if (!window.confirm(`Restore v${version.version} as a new draft?`)) return
    setError(null)
    setMessage(null)
    try {
      const res = await fetch(`/api/admin/sponsor-pages/${encodeURIComponent(slug)}/versions/${encodeURIComponent(version.id)}/restore`, {
        method: 'POST',
        credentials: 'include',
      })
      const data = await res.json() as { draft?: SponsorVersion; error?: string }
      if (!res.ok || !data.draft?.data) throw new Error(data.error ?? 'Unable to restore version')
      setDraftVersion(data.draft)
      setConfig(data.draft.data)
      setDirty(false)
      setMessage(`Restored v${version.version} into draft v${data.draft.version}.`)
      void loadVersions()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to restore version')
    }
  }

  async function archiveVersion(version: SponsorVersion) {
    if (version.state !== 'draft') return
    if (!window.confirm(`Archive draft v${version.version}? It will be hidden from the active version list.`)) return
    setArchivingVersionId(version.id)
    setError(null)
    setMessage(null)
    try {
      const res = await fetch(`/api/admin/sponsor-pages/${encodeURIComponent(slug)}/versions/${encodeURIComponent(version.id)}/archive`, {
        method: 'POST',
        credentials: 'include',
      })
      const data = await res.json() as { version?: SponsorVersion; error?: string }
      if (!res.ok || !data.version) throw new Error(data.error ?? 'Unable to archive draft')
      setMessage(`Archived draft v${data.version.version}.`)
      if (draftVersion?.id === version.id) {
        await loadPage()
      } else {
        void loadVersions()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to archive draft')
    } finally {
      setArchivingVersionId(null)
    }
  }

  async function uploadSponsorMedia(file: File | null, mediaType: 'image' | 'video', blockId: string, applyUrl: (url: string) => void, allowedTypes?: Set<string>) {
    if (!file) return
    setMessage(null)
    setError(null)
    const allowed = allowedTypes ?? (mediaType === 'image' ? ALLOWED_IMAGE_TYPES : ALLOWED_VIDEO_TYPES)
    const maxBytes = mediaType === 'image' ? MAX_HERO_IMAGE_BYTES : MAX_HERO_VIDEO_BYTES
    if (!allowed.has(file.type)) {
      setError(uploadTypeError(mediaType, allowed))
      return
    }
    if (file.size > maxBytes) {
      setError(`File is ${formatFileSize(file.size)}. Keep it under ${formatFileSize(maxBytes)}.`)
      return
    }
    const key = `${blockId}:${mediaType}`
    setUploadProgress({ key, fileName: file.name || 'Selected file', fileSize: file.size, progress: 0 })
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('scope', 'sponsor')
      formData.append('slug', slug)
      formData.append('blockId', blockId)
      formData.append('mediaType', mediaType)
      const data = await uploadMediaWithProgress(formData, progress => {
        setUploadProgress(current => current?.key === key ? { ...current, progress } : current)
      })
      if (!data.url) throw new Error(data.error ?? 'Upload failed')
      if (data.mediaType && data.mediaType !== mediaType) throw new Error('Upload returned the wrong media type for this field.')
      applyUrl(data.url)
      setMessage(`Media uploaded. Save draft to keep it in ${activePitch.label}.`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to upload media')
    } finally {
      setUploadProgress(null)
    }
  }

  function renderUploadProgress(key: string) {
    if (uploadProgress?.key !== key) return null
    const progress = uploadProgress.progress
    return (
      <div id={uploadStatusId(key)} className="admin-upload-progress" role="status" aria-live="polite">
        <div className="admin-upload-progress-meta">
          <strong>{uploadProgress.fileName}</strong>
          <span>{formatFileSize(uploadProgress.fileSize)} / {progress === null ? 'Uploading' : `${progress}%`}</span>
        </div>
        <progress aria-label={`Uploading ${uploadProgress.fileName}`} max={100} {...(progress === null ? {} : { value: progress })} />
      </div>
    )
  }

  if (loading) {
    return <div className="admin-page"><p className="admin-muted">Checking admin access...</p></div>
  }

  if (!isLoggedIn) return <Navigate to="/signin" replace />

  if (!canAdmin) {
    return (
      <div className="admin-page">
        <span className="legal-eyebrow">Admin</span>
        <h1 className="admin-heading">Admin access required.</h1>
        <p className="admin-muted">This page is only available to approved Yan-Vada admins.</p>
        <Link to="/" className="btn btn-secondary">Back to public home</Link>
      </div>
    )
  }

  if (loadingPage) {
    return <div className="admin-page"><div className="admin-card"><p className="admin-muted">Loading {previewMode ? AUDIENCE_PREVIEW_TAB.label : activePitch.label}...</p></div></div>
  }

  if (!config) {
    return <div className="admin-page"><div className="admin-alert admin-alert-error">{error ?? `${activePitch.label} content is unavailable.`}</div></div>
  }

  return (
    <div className="admin-page admin-sponsor-editor-page">
      <header className="admin-header">
        <div>
          <span className="legal-eyebrow">{editorEyebrow}</span>
          <h1 className="admin-heading">{previewMode ? 'Audience Preview' : editorTitle}</h1>
          <p className="admin-muted">
            {previewMode
              ? 'This is the public audience surface derived from the active Master pitch. It is read-only here and always truncates to the Hero plus the first intro section.'
              : activeSlug === 'master'
                ? 'Edit the protected Master sponsor pitch. Save changes as a draft, publish when ready, and preview the protected page at /sponsors/master. Sponsor-specific tabs derive from this version.'
                : `Edit the protected ${activePitch.label} sponsor edition. This tab stems from Master, but keeps its own draft and published history for sponsor-specific changes.`}
          </p>
          <div className="admin-status-strip" aria-label={`${previewMode ? 'Audience preview' : activePitch.label} status`}>
            <span><strong>Source</strong>{sourceStatusLabel(source)}</span>
            <span><strong>Draft</strong>{draftStatusLabel(draftVersion)}</span>
            <span><strong>Published</strong>{publishedStatusLabel(publishedVersion)}</span>
            {!previewMode && activeSlug !== 'master' && <span><strong>Base</strong>{pageMeta?.base_slug ?? 'master'}</span>}
          </div>
        </div>
      </header>

      {(message || error) && (
        <div className={`admin-alert${error ? ' admin-alert-error' : ''}`} role={error ? 'alert' : 'status'}>
          {error ?? message}
        </div>
      )}

      <button
        ref={versionButtonRef}
        className={`admin-version-chrome-button${versionPopoverOpen ? ' open' : ''}`}
        type="button"
        onClick={toggleVersionPopover}
        aria-label={`Open ${previewMode ? 'Master' : activePitch.label} version history`}
        aria-haspopup="dialog"
        aria-expanded={versionPopoverOpen}
        aria-controls="master-pitch-version-popover"
      >
        <VersionIcon />
        <span>Versions</span>
        <strong>{draftVersion ? `v${draftVersion.version}` : (publishedVersion ? `v${publishedVersion.version}` : '0')}</strong>
      </button>

      {versionPopoverOpen && (
        <div
          ref={versionPopoverRef}
          id="master-pitch-version-popover"
          className="admin-version-popover"
          role="dialog"
          aria-label={`${previewMode ? 'Master' : activePitch.label} version history`}
        >
          <div className="admin-version-popover-header">
            <div>
              <span className="legal-eyebrow">Version control</span>
              <strong>{previewMode ? 'Master' : activePitch.label} history</strong>
              <small>{versionsLoading ? 'Loading history' : `${versions.length} recent versions`}</small>
            </div>
            <button className="admin-version-popover-close" type="button" onClick={() => setVersionPopoverOpen(false)} aria-label="Close version history">Close</button>
          </div>

          <div className="admin-version-popover-status" aria-label="Current version status">
            <div><strong>{draftStatusLabel(draftVersion)}</strong><span>Draft</span></div>
            <div><strong>{publishedStatusLabel(publishedVersion)}</strong><span>Published</span></div>
          </div>

          <div className="admin-version-list">
            {versions.length === 0 && <p className="admin-muted">Version history appears here after Supabase bindings and migrations are available.</p>}
            {versions.map(version => {
              const isCurrent = publishedVersion?.id === version.id
              const isDraft = version.state === 'draft'
              const isArchived = version.state === 'archived'
              return (
                <article className={`admin-version-row${isCurrent ? ' current' : ''}`} key={version.id}>
                  <VersionIcon />
                  <div className="admin-version-main">
                    <strong>v{version.version} · {version.state}{isCurrent ? ' · current' : ''}</strong>
                    <span>{version.change_summary || `Saved ${formatDate(version.created_at)}`} · {actorName(version, 'created')}</span>
                    <span>{version.approval_status ?? 'pending'}{version.approved_at ? ` · ${actorName(version, 'approved')}` : ''}{version.archived_at ? ` · archived ${formatDate(version.archived_at)}` : ''}</span>
                  </div>
                  <div className="admin-version-actions" aria-label={`Actions for version ${version.version}`}>
                    {version.approval_status !== 'approved' && !isArchived && <button className="admin-version-action-button" type="button" onClick={() => void approveVersion(version.id)} title={`Approve v${version.version}`} aria-label={`Approve v${version.version}`}><VersionActionIcon type="approve" /><span>Approve</span></button>}
                    {!isArchived && <button className="admin-version-action-button" type="button" onClick={() => void restoreVersion(version)} title={`Restore v${version.version}`} aria-label={`Restore v${version.version}`}><VersionActionIcon type="restore" /><span>Restore</span></button>}
                    {isDraft && <button className="admin-version-action-button" type="button" onClick={() => void archiveVersion(version)} disabled={archivingVersionId === version.id} title={`Archive draft v${version.version}`} aria-label={`Archive draft v${version.version}`}><VersionActionIcon type="archive" /><span>{archivingVersionId === version.id ? 'Archiving' : 'Archive'}</span></button>}
                    {!isArchived && <button className="admin-version-action-button primary" type="button" onClick={() => void publishDraft(version.id)} disabled={publishing} title={`Publish v${version.version}`} aria-label={`Publish v${version.version}`}><VersionActionIcon type="publish" /><span>{publishing ? 'Publishing' : 'Publish'}</span></button>}
                  </div>
                </article>
              )
            })}
          </div>
        </div>
      )}

      <div className="admin-pitch-tabs" role="tablist" aria-label="Pitch variants">
        {SPONSOR_PITCH_TABS.map((tab) => (
          <button
            id={`pitch-tab-${tab.slug}`}
            className={`admin-pitch-tab${activeTab === tab.slug ? ' active' : ''}`}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.slug}
            aria-controls="master-pitch-editor-panel"
            key={tab.slug}
            onClick={() => openEditorTab(tab.slug)}
          >
            <span>{tab.label}</span>
            <small>{tab.description}</small>
          </button>
        ))}
        <button
          id="pitch-tab-audience-preview"
          className={`admin-pitch-tab${activeTab === AUDIENCE_PREVIEW_TAB.key ? ' active' : ''}`}
          type="button"
          role="tab"
          aria-selected={activeTab === AUDIENCE_PREVIEW_TAB.key}
          aria-controls="master-pitch-editor-panel"
          onClick={openAudiencePreview}
        >
          <span>{AUDIENCE_PREVIEW_TAB.label}</span>
          <small>{AUDIENCE_PREVIEW_TAB.description}</small>
        </button>
      </div>

      <section id="master-pitch-editor-panel" role="tabpanel" aria-labelledby="pitch-tab-master">
        {previewMode ? (
          <div className="admin-card">
            <div className="admin-sticky-actions">
              <div>
                <strong>Read-only preview</strong>
                <span>Derived from the current Master pitch content.</span>
              </div>
              <div className="admin-member-actions">
                <Link className="btn btn-secondary" to="/">Open public audience page</Link>
                <button className="btn btn-primary" type="button" onClick={() => openEditorTab('master')}>Back to Master</button>
              </div>
            </div>
            <div className="admin-card admin-audience-preview-shell">
              <main className="sponsor-page" data-surface="branding" data-sponsor="master" data-view-mode="audience-preview">
                <HeroC
                  headline={audiencePreviewConfig.content.hero_headline}
                  seasonLabel={audiencePreviewConfig.content.hero_subhead}
                  showCtaActions
                  sponsorLogo={audiencePreviewConfig.content.hero_sponsor_logo}
                  sponsorName={audiencePreviewConfig.content.hero_sponsor_name}
                  mediaType={audiencePreviewConfig.content.hero_video ? 'video' : 'image'}
                  heroImageUrl={audiencePreviewConfig.content.hero_poster || '/sunset-1.jpeg'}
                  heroVideoUrl={audiencePreviewConfig.content.hero_video || ''}
                  heroVideoWebmUrl={audiencePreviewConfig.content.hero_video_webm || ''}
                  showScrollCue
                  scrollTargetId="admin-audience-preview-content"
                  partnershipDisabled
                  partnershipDisabledLabel="Read Partnership Opportunity Locked"
                  socialLinks={MASTER_HERO_SOCIAL_LINKS}
                />
                <div id="admin-audience-preview-content" className="sponsor-content">
                  {renderAudienceIntro(audiencePreviewConfig)}
                </div>
              </main>
            </div>
          </div>
        ) : (
        <form className="admin-card admin-content-form admin-sponsor-form" onSubmit={e => { e.preventDefault(); void saveDraft() }}>
        <div className="admin-sticky-actions">
          <div>
            <strong>{dirty ? 'Unsaved changes' : (draftVersion ? `Draft v${draftVersion.version}` : 'No draft')}</strong>
            <span>Published: {formatDate(publishedVersion?.published_at)}</span>
          </div>
          <div className="admin-member-actions">
            <button className="btn btn-primary" type="submit" disabled={saving || publishing}>{saving ? 'Saving...' : 'Save draft'}</button>
            <button className="btn btn-secondary" type="button" onClick={() => void publishDraft()} disabled={saving || publishing}>{publishing ? 'Publishing...' : 'Publish draft'}</button>
            <Link className="btn btn-secondary" to={`/sponsors/${activeSlug}`}>View</Link>
          </div>
        </div>

        <AccordionPanel id="sponsor-page-panel" eyebrow="Page" title="Page identity" summary={config.name} chips={[config.tier, config.tracking.campaign_id]} open={openPanels.page} onToggle={() => togglePanel('page')}>
          <div className="admin-content-section admin-content-section-compact">
            <label><span>Name</span><input value={config.name} onChange={e => updateConfig(next => { next.name = e.target.value })} /></label>
            <label><span>Tier</span><input value={config.tier} onChange={e => updateConfig(next => { next.tier = e.target.value })} /></label>
            <label><span>Reply-to</span><input value={config.contact.reply_to} onChange={e => updateConfig(next => { next.contact.reply_to = e.target.value })} /></label>
            <label><span>Campaign ID</span><input value={config.tracking.campaign_id} onChange={e => updateConfig(next => { next.tracking.campaign_id = e.target.value })} /></label>
            <details className="admin-subdetails admin-field-wide">
              <summary>Advanced metadata</summary>
              <div className="admin-sponsor-block-grid">
                <label><span>Provenance ref</span><input value={config._provenance_ref ?? ''} onChange={e => updateConfig(next => { next._provenance_ref = e.target.value })} /></label>
                <label><span>Disclosure policy</span><select value={config._external_disclosure_policy} onChange={e => updateConfig(next => { next._external_disclosure_policy = e.target.value as SponsorConfig['_external_disclosure_policy'] })}><option value="silent">Silent</option><option value="partial">Partial</option><option value="minimal">Minimal</option><option value="full">Full</option></select></label>
                <label className="admin-checkbox"><input type="checkbox" checked={config.tracking.analytics_enabled} onChange={e => updateConfig(next => { next.tracking.analytics_enabled = e.target.checked })} /> Analytics enabled</label>
              </div>
            </details>
          </div>
        </AccordionPanel>

        <AccordionPanel id="sponsor-hero-panel" eyebrow="Hero" title="Hero" summary={config.content.hero_headline} chips={[config.content.hero_video ? 'Video' : 'Image', config.content.hero_poster ? 'Poster set' : 'Poster missing']} open={openPanels.hero} onToggle={() => togglePanel('hero')}>
          <div className="admin-content-section admin-content-section-compact">
            {activeSlug === 'master' ? (
              <>
                <label className="admin-field-wide"><span>Hero headline</span><input value={config.content.hero_headline} onChange={e => updateConfig(next => { next.content.hero_headline = e.target.value })} /></label>
                <label className="admin-field-wide"><span>Hero subhead</span><input value={config.content.hero_subhead} onChange={e => updateConfig(next => { next.content.hero_subhead = e.target.value })} /></label>
              </>
            ) : (
              <>
                <label className="admin-field-wide"><span>Hero headline</span><input value={config.content.hero_headline} readOnly /></label>
                <label className="admin-field-wide"><span>Hero subhead</span><input value={config.content.hero_subhead} readOnly /></label>
                <p className="admin-muted admin-field-wide">Hero headline and season label are inherited from Master. Sponsor editions can override sponsor logo, sponsor name, and hero media only.</p>
              </>
            )}
            <label><span>Sponsor logo</span><input value={config.content.hero_sponsor_logo ?? ''} onChange={e => updateConfig(next => { next.content.hero_sponsor_logo = e.target.value })} placeholder="/licensed-logotype.jpeg" /></label>
            <label><span>Sponsor name</span><input value={config.content.hero_sponsor_name ?? ''} onChange={e => updateConfig(next => { next.content.hero_sponsor_name = e.target.value })} /></label>
            <div className="admin-upload-field">
              <label htmlFor="sponsor-logo-upload"><span>Upload sponsor logo</span></label>
              <input id="sponsor-logo-upload" type="file" accept={IMAGE_ACCEPT} disabled={uploadingKey === 'hero-logo:image'} aria-describedby={uploadingKey === 'hero-logo:image' ? uploadStatusId('hero-logo:image') : undefined} onChange={e => { const file = e.currentTarget.files?.[0] ?? null; e.currentTarget.value = ''; void uploadSponsorMedia(file, 'image', 'hero-logo', url => updateConfig(next => { next.content.hero_sponsor_logo = url })) }} />
              {renderUploadProgress('hero-logo:image')}
            </div>
            <label><span>Hero poster/image</span><input value={config.content.hero_poster ?? ''} onChange={e => updateConfig(next => { next.content.hero_poster = e.target.value })} placeholder="/sunset-1.jpeg" /></label>
            <label><span>Hero video MP4</span><input value={config.content.hero_video ?? ''} onChange={e => updateConfig(next => { next.content.hero_video = e.target.value })} /></label>
            <div className="admin-upload-field">
              <label htmlFor="sponsor-hero-image"><span>Upload hero image</span></label>
              <input id="sponsor-hero-image" type="file" accept={IMAGE_ACCEPT} disabled={uploadingKey === 'hero:image'} aria-describedby={uploadingKey === 'hero:image' ? uploadStatusId('hero:image') : undefined} onChange={e => { const file = e.currentTarget.files?.[0] ?? null; e.currentTarget.value = ''; void uploadSponsorMedia(file, 'image', 'hero', url => updateConfig(next => { next.content.hero_poster = url })) }} />
              {renderUploadProgress('hero:image')}
            </div>
            <div className="admin-upload-field">
              <label htmlFor="sponsor-hero-video"><span>Upload hero video</span></label>
              <input id="sponsor-hero-video" type="file" accept={PRIMARY_VIDEO_ACCEPT} disabled={uploadingKey === 'hero:video'} aria-describedby={uploadingKey === 'hero:video' ? uploadStatusId('hero:video') : undefined} onChange={e => { const file = e.currentTarget.files?.[0] ?? null; e.currentTarget.value = ''; void uploadSponsorMedia(file, 'video', 'hero', url => updateConfig(next => { next.content.hero_video = url; next.content.hero_video_webm = '' }), ALLOWED_PRIMARY_VIDEO_TYPES) }} />
              {renderUploadProgress('hero:video')}
            </div>
            <details className="admin-subdetails admin-field-wide">
              <summary>Advanced video fallback</summary>
              <div className="admin-sponsor-block-grid">
                <label><span>Hero video WebM</span><input value={config.content.hero_video_webm ?? ''} onChange={e => updateConfig(next => { next.content.hero_video_webm = e.target.value })} /></label>
                <p className="admin-muted admin-field-wide">Optional WebM source used only when explicitly set. Uploading a new primary video clears this field to avoid stale fallbacks.</p>
              </div>
            </details>
          </div>
        </AccordionPanel>

        <AccordionPanel id="sponsor-brand-panel" eyebrow="Brand" title="Brand defaults" summary={config.brand.logo_alt} chips={[config.brand.primary_colour, config.brand.accent_colour, config.brand.background_preference]} open={openPanels.brand} onToggle={() => togglePanel('brand')}>
          <div className="admin-content-section admin-content-section-compact">
            <label><span>Primary colour</span><input value={config.brand.primary_colour} onChange={e => updateConfig(next => { next.brand.primary_colour = e.target.value })} /></label>
            <label><span>Accent colour</span><input value={config.brand.accent_colour} onChange={e => updateConfig(next => { next.brand.accent_colour = e.target.value })} /></label>
            <label><span>Logo asset</span><input value={config.brand.logo_asset} onChange={e => updateConfig(next => { next.brand.logo_asset = e.target.value })} /></label>
            <label><span>Logo alt</span><input value={config.brand.logo_alt} onChange={e => updateConfig(next => { next.brand.logo_alt = e.target.value })} /></label>
            <label><span>Background</span><select value={config.brand.background_preference} onChange={e => updateConfig(next => { next.brand.background_preference = e.target.value as SponsorConfig['brand']['background_preference'] })}><option value="dark">Dark</option><option value="light">Light</option><option value="auto">Auto</option></select></label>
          </div>
        </AccordionPanel>

        <AccordionPanel id="sponsor-layout-panel" eyebrow="Hero layout" title="Hero logo position" summary="Position the × and sponsor logo with px dials" chips={[config.content.hero_layout ? 'Custom' : 'Defaults', config.content.hero_sponsor_logo ? 'Logo set' : 'No logo']} open={openPanels.layout} onToggle={() => togglePanel('layout')}>
          <div className="admin-content-section admin-content-section-compact">
            <p className="admin-muted admin-field-wide">Nudge the co-brand lockup in pixels. Leave a field blank to use the default. Changes show live in the preview below; Save draft + Publish to deploy. Only this sponsor page is affected.</p>
            <label><span>Logo nudge X (px)</span><input type="number" step={1} value={pxToNum(config.content.hero_layout?.sponsor_shift_x)} onChange={e => setLayoutPx('sponsor_shift_x', e.target.value)} placeholder="default" /></label>
            <label><span>× nudge X (px)</span><input type="number" step={1} value={pxToNum(config.content.hero_layout?.separator_offset_x)} onChange={e => setLayoutPx('separator_offset_x', e.target.value)} placeholder="default" /></label>
            <label><span>Vertical align (px)</span><input type="number" step={1} value={pxToNum(config.content.hero_layout?.midline_shift)} onChange={e => setLayoutPx('midline_shift', e.target.value)} placeholder="default" /></label>
            <label><span>Logo height (px)</span><input type="number" step={1} min={0} value={pxToNum(config.content.hero_layout?.logo_max_height)} onChange={e => setLayoutPx('logo_max_height', e.target.value)} placeholder="default" /></label>
            <label><span>× colour</span><input value={config.content.hero_layout?.separator_color ?? ''} onChange={e => setLayoutField('separator_color', e.target.value)} placeholder="e.g. rgba(245,241,234,0.96)" /></label>
            <div className="admin-member-actions admin-field-wide">
              <button className="btn btn-secondary" type="button" onClick={() => updateConfig(next => { next.content.hero_layout = undefined })}>Reset to defaults</button>
            </div>
            <div className="admin-field-wide" aria-label="Hero layout live preview" style={{ marginTop: 12, height: 360, overflow: 'hidden', borderRadius: 12, border: '1px solid rgba(0,0,0,0.12)', position: 'relative' }}>
              <div style={{ transform: 'scale(0.4)', transformOrigin: 'top center', width: '250%', marginLeft: '-75%', pointerEvents: 'none' }}>
                <HeroC
                  compact
                  headline={config.content.hero_headline}
                  seasonLabel={config.content.hero_subhead}
                  sponsorLogo={config.content.hero_sponsor_logo}
                  sponsorName={config.content.hero_sponsor_name}
                  heroLayout={config.content.hero_layout}
                  mediaType={config.content.hero_video ? 'video' : 'image'}
                  heroImageUrl={config.content.hero_poster || '/sunset-1.jpeg'}
                />
              </div>
            </div>
          </div>
        </AccordionPanel>

        <AccordionPanel id="sponsor-sections-panel" eyebrow="Sections" title="Pitch sections" summary={`${config.content.sections.length} visible blocks`} chips={[dirty ? 'Unsaved' : 'Saved', `${config.content.sections.length} blocks`]} open={openPanels.sections} onToggle={() => togglePanel('sections')}>
          <div className="admin-sponsor-blocks" aria-label="Master Pitch sections">
            <div className="admin-sponsor-blocks-header">
              <p className="admin-muted">Collapsed headers are the scan view. Open only the block you are editing.</p>
              <div className="admin-member-actions">
                <button className="btn btn-secondary" type="button" onClick={() => updateConfig(next => { next.content.sections.push(newSection('text')) })}>Add text</button>
                <button className="btn btn-secondary" type="button" onClick={() => updateConfig(next => { next.content.sections.push(newSection('socials')) })}>Add socials</button>
                <button className="btn btn-secondary" type="button" onClick={() => updateConfig(next => { next.content.sections.push(newSection('value-proof')) })}>Add value proof</button>
                <button className="btn btn-secondary" type="button" onClick={() => updateConfig(next => { next.content.sections.push(newSection('media')) })}>Add media</button>
                <button className="btn btn-secondary" type="button" onClick={() => updateConfig(next => { next.content.sections.push(newSection('image-grid')) })}>Add images</button>
              </div>
            </div>

            {config.content.sections.map((section, index) => {
              const key = sectionKey(section, index)
              const open = openSections[key] ?? index === 0
              return (
                <article className="admin-sponsor-block" key={key}>
                  <header className="admin-sponsor-block-header">
                    <button className="admin-sponsor-block-toggle" type="button" aria-expanded={open} aria-controls={`${key}-editor`} onClick={() => toggleSection(key)}>
                      <span className="legal-eyebrow">{String(index + 1).padStart(2, '0')} · {section.type}</span>
                      <strong>{sectionLabel(section, index)}</strong>
                      <span className="admin-chip-row">{sectionChips(section).map(chip => <span className="admin-chip" key={chip}>{chip}</span>)}</span>
                    </button>
                    <div className="admin-member-actions">
                      <button className="btn btn-ghost" type="button" onClick={() => moveSection(index, -1)} disabled={index === 0}>Up</button>
                      <button className="btn btn-ghost" type="button" onClick={() => moveSection(index, 1)} disabled={index === config.content.sections.length - 1}>Down</button>
                      <button className="btn btn-danger" type="button" onClick={() => removeSection(index)}>Remove section</button>
                    </div>
                  </header>
                  {open && (
                    <div id={`${key}-editor`} className="admin-sponsor-block-grid">
                      {renderSectionEditor(section, index)}
                    </div>
                  )}
                </article>
              )
            })}
          </div>
        </AccordionPanel>

        <AccordionPanel id="sponsor-notes-panel" eyebrow="Internal" title="Internal notes" summary={(config._notes ?? '').slice(0, 80) || 'No notes'} chips={[`${countWords(config._notes ?? '')} words`]} open={openPanels.notes} onToggle={() => togglePanel('notes')}>
          <div className="admin-content-section admin-content-section-compact">
            <label className="admin-field-wide"><span>Notes</span><textarea rows={4} value={config._notes ?? ''} onChange={e => updateConfig(next => { next._notes = e.target.value })} /></label>
          </div>
        </AccordionPanel>
        </form>
        )}
      </section>
    </div>
  )

  function renderSectionEditor(section: ContentSection, index: number) {
    if (section.type === 'text') {
      const text = section as TextSection
      return (
        <>
          <label><span>Heading</span><input value={text.heading ?? ''} onChange={e => updateSection(index, { ...text, heading: e.target.value })} /></label>
          <label className="admin-field-wide"><span>Body</span><textarea rows={8} value={text.body} onChange={e => updateSection(index, { ...text, body: e.target.value })} /></label>
          <details className="admin-subdetails admin-field-wide"><summary>Advanced metadata</summary><label><span>Block ID</span><input value={text.id ?? ''} onChange={e => updateSection(index, { ...text, id: e.target.value })} /></label></details>
        </>
      )
    }

    if (section.type === 'socials') {
      const socials = section as SocialsSection
      return (
        <>
          <label><span>Heading</span><input value={socials.heading} onChange={e => updateSection(index, { ...socials, heading: e.target.value })} /></label>
          <label className="admin-field-wide"><span>Body</span><textarea rows={3} value={socials.body ?? ''} onChange={e => updateSection(index, { ...socials, body: e.target.value })} /></label>
          <label className="admin-field-wide"><span>Report links, one label | URL per line</span><textarea rows={3} value={formatLinks(socials.links)} onChange={e => updateSection(index, { ...socials, links: pipeLinkList(e.target.value) })} /></label>
          <div className="admin-nested-list admin-field-wide">
            {socials.platforms.map((platform, platformIndex) => {
              const platformKey = platform.platform ?? inferPlatform(platform.label, platform.href)
              return (
              <div className="admin-nested-item admin-social-platform-item" data-platform={platformKey} key={platformIndex}>
                <div className="admin-nested-item-title admin-field-wide">
                  <span className="socials-icon-wrap"><PlatformIcon platform={platformKey} /></span>
                  <strong>{platform.label || platformLabel(platformKey)}</strong>
                  <span>{platform.metric_value || 'Metric pending'}</span>
                </div>
                <label><span>Platform</span><select value={platformKey} onChange={e => { const selected = e.target.value as SocialPlatform; const platforms = [...socials.platforms]; platforms[platformIndex] = { ...platform, platform: selected, label: !platform.label || platform.label === 'New platform' ? platformLabel(selected) : platform.label }; updateSection(index, { ...socials, platforms }) }}>{SOCIAL_PLATFORMS.map(key => <option value={key} key={key}>{platformLabel(key)}</option>)}</select></label>
                <label><span>Label</span><input value={platform.label} onChange={e => { const platforms = [...socials.platforms]; platforms[platformIndex] = { ...platform, label: e.target.value }; updateSection(index, { ...socials, platforms }) }} /></label>
                <label><span>Metric</span><input value={platform.metric_value ?? ''} onChange={e => { const platforms = [...socials.platforms]; platforms[platformIndex] = { ...platform, metric_value: e.target.value }; updateSection(index, { ...socials, platforms }) }} /></label>
                <label><span>Metric label</span><input value={platform.metric_label ?? ''} onChange={e => { const platforms = [...socials.platforms]; platforms[platformIndex] = { ...platform, metric_label: e.target.value }; updateSection(index, { ...socials, platforms }) }} /></label>
                <label className="admin-field-wide"><span>Link</span><input value={platform.href ?? ''} onChange={e => { const platforms = [...socials.platforms]; platforms[platformIndex] = { ...platform, href: e.target.value }; updateSection(index, { ...socials, platforms }) }} /></label>
                <button className="btn btn-ghost" type="button" onClick={() => updateSection(index, { ...socials, platforms: socials.platforms.filter((_, i) => i !== platformIndex) })}>Remove platform</button>
              </div>
              )
            })}
            <button className="btn btn-secondary" type="button" onClick={() => updateSection(index, { ...socials, platforms: [...socials.platforms, { platform: 'other', label: 'New platform', metric_label: '', metric_value: '' }] })}>Add platform</button>
          </div>
          <details className="admin-subdetails admin-field-wide"><summary>Advanced metadata</summary><label><span>Block ID</span><input value={socials.id ?? ''} onChange={e => updateSection(index, { ...socials, id: e.target.value })} /></label></details>
        </>
      )
    }

    if (section.type === 'value-proof') {
      const valueProof = section as ValueProofSection
      const currentView: NonNullable<ValueProofSection['current_view']> = valueProof.current_view ?? { label: 'Current view surface', stat: '', body: '', items: [] }
      return (
        <>
          <label><span>Eyebrow</span><input value={valueProof.eyebrow ?? ''} onChange={e => updateSection(index, { ...valueProof, eyebrow: e.target.value })} /></label>
          <label><span>Heading</span><input value={valueProof.heading} onChange={e => updateSection(index, { ...valueProof, heading: e.target.value })} /></label>
          <label className="admin-field-wide"><span>Body</span><textarea rows={4} value={valueProof.body ?? ''} onChange={e => updateSection(index, { ...valueProof, body: e.target.value })} /></label>
          <label><span>CTA label</span><input value={valueProof.action?.label ?? ''} onChange={e => updateSection(index, { ...valueProof, action: { label: e.target.value, href: valueProof.action?.href ?? '' } })} /></label>
          <label><span>CTA href</span><input value={valueProof.action?.href ?? ''} onChange={e => updateSection(index, { ...valueProof, action: { label: valueProof.action?.label ?? '', href: e.target.value } })} /></label>
          <div className="admin-nested-list admin-field-wide">
            <div className="admin-nested-item-title admin-field-wide">
              <strong>Yan-Vada sponsor value report cards</strong>
              <span>Keep this section limited to the value-report title, labels, numbers, and anchors.</span>
            </div>
            {valueProof.cards.map((card, cardIndex) => (
              <div className="admin-nested-item" key={cardIndex}>
                <div className="admin-nested-item-title admin-field-wide">
                  <strong>{card.label || `Proof card ${cardIndex + 1}`}</strong>
                  <span>{card.stat || 'Number pending'}</span>
                </div>
                <label><span>Label</span><input value={card.label} onChange={e => { const cards = [...valueProof.cards]; cards[cardIndex] = { ...card, label: e.target.value }; updateSection(index, { ...valueProof, cards }) }} /></label>
                <label><span>Number</span><input value={card.stat} onChange={e => { const cards = [...valueProof.cards]; cards[cardIndex] = { ...card, stat: e.target.value }; updateSection(index, { ...valueProof, cards }) }} /></label>
                <label className="admin-field-wide"><span>Card body</span><textarea rows={2} value={card.body ?? ''} onChange={e => { const cards = [...valueProof.cards]; cards[cardIndex] = { ...card, body: e.target.value }; updateSection(index, { ...valueProof, cards }) }} /></label>
                <label className="admin-field-wide"><span>Value report anchor / link</span><input value={card.href ?? ''} onChange={e => { const cards = [...valueProof.cards]; cards[cardIndex] = { ...card, href: e.target.value }; updateSection(index, { ...valueProof, cards }) }} /></label>
                <label className="admin-checkbox"><input type="checkbox" checked={card.featured === true} onChange={e => { const cards = [...valueProof.cards]; cards[cardIndex] = { ...card, featured: e.target.checked }; updateSection(index, { ...valueProof, cards }) }} /> Featured card</label>
                <label className="admin-field-wide"><span>Supporting metrics, one label | value per line</span><textarea rows={3} value={formatValueProofMetrics(card.metrics)} onChange={e => { const cards = [...valueProof.cards]; cards[cardIndex] = { ...card, metrics: pipeValueProofMetrics(e.target.value) }; updateSection(index, { ...valueProof, cards }) }} /></label>
                <button className="btn btn-ghost" type="button" onClick={() => updateSection(index, { ...valueProof, cards: valueProof.cards.filter((_, i) => i !== cardIndex) })}>Remove proof card</button>
              </div>
            ))}
            <button className="btn btn-secondary" type="button" onClick={() => updateSection(index, { ...valueProof, cards: [...valueProof.cards, { label: 'New proof label', stat: '', body: '', href: '' }] })}>Add proof card</button>
          </div>
          <div className="admin-nested-list admin-field-wide">
            <div className="admin-nested-item-title admin-field-wide">
              <strong>Current view surface panel</strong>
              <span>Backs the sponsor social media flow without editing the whole static report.</span>
            </div>
            <label><span>Panel label</span><input value={currentView.label} onChange={e => updateSection(index, { ...valueProof, current_view: { ...currentView, label: e.target.value } })} /></label>
            <label><span>Main number</span><input value={currentView.stat} onChange={e => updateSection(index, { ...valueProof, current_view: { ...currentView, stat: e.target.value } })} /></label>
            <label className="admin-field-wide"><span>Panel body</span><textarea rows={2} value={currentView.body ?? ''} onChange={e => updateSection(index, { ...valueProof, current_view: { ...currentView, body: e.target.value } })} /></label>
            <label className="admin-field-wide"><span>Value report anchor / link</span><input value={currentView.href ?? ''} onChange={e => updateSection(index, { ...valueProof, current_view: { ...currentView, href: e.target.value } })} /></label>
            <label className="admin-field-wide"><span>Context note</span><textarea rows={2} value={currentView.note ?? ''} onChange={e => updateSection(index, { ...valueProof, current_view: { ...currentView, note: e.target.value } })} /></label>
            {currentView.items.map((item, itemIndex) => (
              <div className="admin-nested-item" key={itemIndex}>
                <div className="admin-nested-item-title admin-field-wide">
                  <strong>{item.label || `View item ${itemIndex + 1}`}</strong>
                  <span>{item.value || 'Value pending'}</span>
                </div>
                <label><span>Label</span><input value={item.label} onChange={e => { const items = [...currentView.items]; items[itemIndex] = { ...item, label: e.target.value }; updateSection(index, { ...valueProof, current_view: { ...currentView, items } }) }} /></label>
                <label><span>Value</span><input value={item.value} onChange={e => { const items = [...currentView.items]; items[itemIndex] = { ...item, value: e.target.value }; updateSection(index, { ...valueProof, current_view: { ...currentView, items } }) }} /></label>
                <label><span>Period</span><input value={item.period ?? ''} onChange={e => { const items = [...currentView.items]; items[itemIndex] = { ...item, period: e.target.value }; updateSection(index, { ...valueProof, current_view: { ...currentView, items } }) }} /></label>
                <label className="admin-field-wide"><span>Value report anchor / link</span><input value={item.href ?? ''} onChange={e => { const items = [...currentView.items]; items[itemIndex] = { ...item, href: e.target.value }; updateSection(index, { ...valueProof, current_view: { ...currentView, items } }) }} /></label>
                <button className="btn btn-ghost" type="button" onClick={() => updateSection(index, { ...valueProof, current_view: { ...currentView, items: currentView.items.filter((_, i) => i !== itemIndex) } })}>Remove view item</button>
              </div>
            ))}
            <button className="btn btn-secondary" type="button" onClick={() => updateSection(index, { ...valueProof, current_view: { ...currentView, items: [...currentView.items, { label: 'New source', value: '', period: '', href: '' }] } })}>Add view item</button>
          </div>
          <details className="admin-subdetails admin-field-wide"><summary>Advanced JSON</summary><label><span>Value proof JSON</span><textarea rows={14} value={JSON.stringify(valueProof, null, 2)} onChange={e => {
            try {
              const parsed = JSON.parse(e.target.value) as ValueProofSection
              if (parsed?.type === 'value-proof') updateSection(index, parsed)
            } catch {
              // Keep the last valid section while the JSON is mid-edit.
            }
          }} /></label></details>
          <details className="admin-subdetails admin-field-wide"><summary>Advanced metadata</summary><label><span>Block ID</span><input value={valueProof.id ?? ''} onChange={e => updateSection(index, { ...valueProof, id: e.target.value })} /></label></details>
        </>
      )
    }

    if (section.type === 'benefit-list') {
      const benefitList = section as BenefitListSection
      return (
        <>
          <label><span>Heading</span><input value={benefitList.heading} onChange={e => updateSection(index, { ...benefitList, heading: e.target.value })} /></label>
          <label className="admin-field-wide"><span>Body</span><textarea rows={3} value={benefitList.body ?? ''} onChange={e => updateSection(index, { ...benefitList, body: e.target.value })} /></label>
          <div className="admin-nested-list admin-field-wide">
            {benefitList.benefits.map((benefit, benefitIndex) => (
              <div className="admin-nested-item" key={benefitIndex}>
                <label><span>Name</span><input value={benefit.name} onChange={e => { const benefits = [...benefitList.benefits]; benefits[benefitIndex] = { ...benefit, name: e.target.value }; updateSection(index, { ...benefitList, benefits }) }} /></label>
                <label className="admin-field-wide"><span>Description</span><textarea rows={3} value={benefit.description} onChange={e => { const benefits = [...benefitList.benefits]; benefits[benefitIndex] = { ...benefit, description: e.target.value }; updateSection(index, { ...benefitList, benefits }) }} /></label>
                <button className="btn btn-ghost" type="button" onClick={() => updateSection(index, { ...benefitList, benefits: benefitList.benefits.filter((_, i) => i !== benefitIndex) })}>Remove item</button>
              </div>
            ))}
            <button className="btn btn-secondary" type="button" onClick={() => updateSection(index, { ...benefitList, benefits: [...benefitList.benefits, { name: 'New item', description: '' }] })}>Add item</button>
          </div>
          <details className="admin-subdetails admin-field-wide"><summary>Advanced metadata</summary><label><span>Block ID</span><input value={benefitList.id ?? ''} onChange={e => updateSection(index, { ...benefitList, id: e.target.value })} /></label></details>
        </>
      )
    }

    if (section.type === 'guest-roster') {
      const guestRoster = section as GuestRosterSection
      return (
        <>
          <label><span>Heading</span><input value={guestRoster.heading} onChange={e => updateSection(index, { ...guestRoster, heading: e.target.value })} /></label>
          <label className="admin-field-wide"><span>Body</span><textarea rows={3} value={guestRoster.body ?? ''} onChange={e => updateSection(index, { ...guestRoster, body: e.target.value })} /></label>
          <div className="admin-nested-list admin-field-wide">
            {guestRoster.guests.map((guest, guestIndex) => (
              <div className="admin-nested-item" key={guestIndex}>
                <div className="admin-nested-item-title admin-field-wide"><strong>{guest.name || `Guest ${guestIndex + 1}`}</strong><span>{guest.category}</span></div>
                <label><span>Name</span><input value={guest.name} onChange={e => { const guests = [...guestRoster.guests]; guests[guestIndex] = { ...guest, name: e.target.value }; updateSection(index, { ...guestRoster, guests }) }} /></label>
                <label><span>Reach</span><input value={guest.reach} onChange={e => { const guests = [...guestRoster.guests]; guests[guestIndex] = { ...guest, reach: e.target.value }; updateSection(index, { ...guestRoster, guests }) }} /></label>
                <label><span>Category</span><input value={guest.category} onChange={e => { const guests = [...guestRoster.guests]; guests[guestIndex] = { ...guest, category: e.target.value }; updateSection(index, { ...guestRoster, guests }) }} /></label>
                <button className="btn btn-ghost" type="button" onClick={() => updateSection(index, { ...guestRoster, guests: guestRoster.guests.filter((_, i) => i !== guestIndex) })}>Remove guest</button>
              </div>
            ))}
            <button className="btn btn-secondary" type="button" onClick={() => updateSection(index, { ...guestRoster, guests: [...guestRoster.guests, { name: 'New guest', reach: '', category: '' }] })}>Add guest</button>
          </div>
          <details className="admin-subdetails admin-field-wide"><summary>Advanced metadata</summary><label><span>Block ID</span><input value={guestRoster.id ?? ''} onChange={e => updateSection(index, { ...guestRoster, id: e.target.value })} /></label></details>
        </>
      )
    }

    if (section.type === 'economics') {
      const economics = section as EconomicsSection
      return (
        <>
          <label><span>Heading</span><input value={economics.heading} onChange={e => updateSection(index, { ...economics, heading: e.target.value })} /></label>
          <label className="admin-field-wide"><span>Body</span><textarea rows={3} value={economics.body ?? ''} onChange={e => updateSection(index, { ...economics, body: e.target.value })} /></label>
          <div className="admin-nested-list admin-field-wide">
            {economics.metrics.map((metric, metricIndex) => (
              <div className="admin-nested-item" key={metricIndex}>
                <div className="admin-nested-item-title admin-field-wide"><strong>{metric.label || `Metric ${metricIndex + 1}`}</strong><span>{metric.value}</span></div>
                <label><span>Label</span><input value={metric.label} onChange={e => { const metrics = [...economics.metrics]; metrics[metricIndex] = { ...metric, label: e.target.value }; updateSection(index, { ...economics, metrics }) }} /></label>
                <label><span>Value</span><input value={metric.value} onChange={e => { const metrics = [...economics.metrics]; metrics[metricIndex] = { ...metric, value: e.target.value }; updateSection(index, { ...economics, metrics }) }} /></label>
                <button className="btn btn-ghost" type="button" onClick={() => updateSection(index, { ...economics, metrics: economics.metrics.filter((_, i) => i !== metricIndex) })}>Remove metric</button>
              </div>
            ))}
            <button className="btn btn-secondary" type="button" onClick={() => updateSection(index, { ...economics, metrics: [...economics.metrics, { label: 'New metric', value: '' }] })}>Add metric</button>
          </div>
          <details className="admin-subdetails admin-field-wide"><summary>Advanced metadata</summary><label><span>Block ID</span><input value={economics.id ?? ''} onChange={e => updateSection(index, { ...economics, id: e.target.value })} /></label></details>
        </>
      )
    }

    if (section.type === 'package-block') {
      const packageBlock = section as PackageBlockSection
      return (
        <>
          <label><span>Heading</span><input value={packageBlock.heading ?? ''} onChange={e => updateSection(index, { ...packageBlock, heading: e.target.value })} /></label>
          <label className="admin-field-wide"><span>Intro body</span><textarea rows={3} value={packageBlock.body ?? ''} onChange={e => updateSection(index, { ...packageBlock, body: e.target.value })} /></label>
          <div className="admin-nested-list admin-field-wide">
            {packageBlock.packages.map((pkg, packageIndex) => (
              <div className="admin-nested-item" key={packageIndex}>
                <label><span>Name</span><input value={pkg.name} onChange={e => { const packages = [...packageBlock.packages]; packages[packageIndex] = { ...pkg, name: e.target.value }; updateSection(index, { ...packageBlock, packages }) }} /></label>
                <label><span>Price</span><input value={pkg.price} onChange={e => { const packages = [...packageBlock.packages]; packages[packageIndex] = { ...pkg, price: e.target.value }; updateSection(index, { ...packageBlock, packages }) }} /></label>
                <label><span>Price note</span><input value={pkg.price_note ?? ''} onChange={e => { const packages = [...packageBlock.packages]; packages[packageIndex] = { ...pkg, price_note: e.target.value }; updateSection(index, { ...packageBlock, packages }) }} /></label>
                <label><span>Tagline</span><input value={pkg.tagline ?? ''} onChange={e => { const packages = [...packageBlock.packages]; packages[packageIndex] = { ...pkg, tagline: e.target.value }; updateSection(index, { ...packageBlock, packages }) }} /></label>
                <label className="admin-field-wide"><span>Benefits, one per line</span><textarea rows={7} value={pkg.benefits.join('\n')} onChange={e => { const packages = [...packageBlock.packages]; packages[packageIndex] = { ...pkg, benefits: lineList(e.target.value) }; updateSection(index, { ...packageBlock, packages }) }} /></label>
                <label><span>Footer</span><input value={pkg.footer ?? ''} onChange={e => { const packages = [...packageBlock.packages]; packages[packageIndex] = { ...pkg, footer: e.target.value }; updateSection(index, { ...packageBlock, packages }) }} /></label>
                <button className="btn btn-ghost" type="button" onClick={() => updateSection(index, { ...packageBlock, packages: packageBlock.packages.filter((_, i) => i !== packageIndex) })}>Remove package</button>
              </div>
            ))}
            <button className="btn btn-secondary" type="button" onClick={() => updateSection(index, { ...packageBlock, packages: [...packageBlock.packages, { name: 'New package', price: '', benefits: [] }] })}>Add package</button>
          </div>
          <details className="admin-subdetails admin-field-wide"><summary>Advanced metadata</summary><label><span>Block ID</span><input value={packageBlock.id ?? ''} onChange={e => updateSection(index, { ...packageBlock, id: e.target.value })} /></label></details>
        </>
      )
    }

    if (section.type === 'media') {
      const media = section as MediaSection
      const blockId = media.id ?? `media-${index}`
      return (
        <>
          <label><span>Heading</span><input value={media.heading ?? ''} onChange={e => updateSection(index, { ...media, heading: e.target.value })} /></label>
          <label><span>Media type</span><select value={media.media_type} onChange={e => updateSection(index, { ...media, media_type: e.target.value as MediaSection['media_type'] })}><option value="image">Image</option><option value="video">Video</option></select></label>
          <label className="admin-field-wide"><span>Body</span><textarea rows={3} value={media.body ?? ''} onChange={e => updateSection(index, { ...media, body: e.target.value })} /></label>
          <label><span>URL</span><input value={media.url} onChange={e => updateSection(index, { ...media, url: e.target.value })} /></label>
          <label><span>Poster</span><input value={media.poster ?? ''} onChange={e => updateSection(index, { ...media, poster: e.target.value })} /></label>
          <label><span>Alt</span><input value={media.alt ?? ''} onChange={e => updateSection(index, { ...media, alt: e.target.value })} /></label>
          <label><span>Caption</span><input value={media.caption ?? ''} onChange={e => updateSection(index, { ...media, caption: e.target.value })} /></label>
          <div className="admin-upload-field">
            <label htmlFor={`${blockId}-upload`}><span>Upload media</span></label>
              <input id={`${blockId}-upload`} type="file" accept={acceptForMediaType(media.media_type)} disabled={uploadingKey === `${blockId}:${media.media_type}`} onChange={e => { const file = e.currentTarget.files?.[0] ?? null; e.currentTarget.value = ''; void uploadSponsorMedia(file, media.media_type, blockId, url => updateSection(index, { ...media, url })) }} />
            </div>
          <details className="admin-subdetails admin-field-wide"><summary>Advanced metadata</summary><label><span>Block ID</span><input value={media.id ?? ''} onChange={e => updateSection(index, { ...media, id: e.target.value })} /></label></details>
        </>
      )
    }

    if (section.type === 'image-grid') {
      const grid = section as ImageGridSection
      return (
        <>
          <label><span>Heading</span><input value={grid.heading ?? ''} onChange={e => updateSection(index, { ...grid, heading: e.target.value })} /></label>
          <label className="admin-field-wide"><span>Body</span><textarea rows={3} value={grid.body ?? ''} onChange={e => updateSection(index, { ...grid, body: e.target.value })} /></label>
          <div className="admin-nested-list admin-field-wide">
            {grid.images.map((image, imageIndex) => {
              const imageKey = `${grid.id ?? 'image-grid'}-${imageIndex}`
              return (
                <div className="admin-nested-item" key={imageIndex}>
                  <label><span>URL</span><input value={image.url} onChange={e => { const images = [...grid.images]; images[imageIndex] = { ...image, url: e.target.value }; updateSection(index, { ...grid, images }) }} /></label>
                  <label><span>Alt</span><input value={image.alt} onChange={e => { const images = [...grid.images]; images[imageIndex] = { ...image, alt: e.target.value }; updateSection(index, { ...grid, images }) }} /></label>
                  <label><span>Caption</span><input value={image.caption ?? ''} onChange={e => { const images = [...grid.images]; images[imageIndex] = { ...image, caption: e.target.value }; updateSection(index, { ...grid, images }) }} /></label>
                  <input type="file" accept={IMAGE_ACCEPT} disabled={uploadingKey === `${imageKey}:image`} onChange={e => { const file = e.currentTarget.files?.[0] ?? null; e.currentTarget.value = ''; void uploadSponsorMedia(file, 'image', imageKey, url => { const images = [...grid.images]; images[imageIndex] = { ...image, url }; updateSection(index, { ...grid, images }) }) }} />
                  <button className="btn btn-ghost" type="button" onClick={() => updateSection(index, { ...grid, images: grid.images.filter((_, i) => i !== imageIndex) })}>Remove image</button>
                </div>
              )
            })}
            <button className="btn btn-secondary" type="button" onClick={() => updateSection(index, { ...grid, images: [...grid.images, { url: '', alt: '', caption: '' }] })}>Add image</button>
          </div>
          <details className="admin-subdetails admin-field-wide"><summary>Advanced metadata</summary><label><span>Block ID</span><input value={grid.id ?? ''} onChange={e => updateSection(index, { ...grid, id: e.target.value })} /></label></details>
        </>
      )
    }

    if (section.type === 'quote') {
      return (
        <>
          <label className="admin-field-wide"><span>Quote</span><textarea rows={4} value={section.text} onChange={e => updateSection(index, { ...section, text: e.target.value })} /></label>
          <label><span>Attribution</span><input value={section.attribution ?? ''} onChange={e => updateSection(index, { ...section, attribution: e.target.value })} /></label>
          <details className="admin-subdetails admin-field-wide"><summary>Advanced metadata</summary><label><span>Block ID</span><input value={section.id ?? ''} onChange={e => updateSection(index, { ...section, id: e.target.value })} /></label></details>
        </>
      )
    }

    const unsupported = section as { type?: string }
    return <p className="admin-muted">Unsupported block type: {unsupported.type ?? 'unknown'}</p>
  }
}
